// libraries
import PropTypes from 'prop-types'
import React from 'react'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'

// dependencies
import { Button, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'

// lib
import Events from '@st-operations/uikit/Events.js'
import formatter from '@st-operations/uikit/lib/format.js'

// css
import './DraggableList.scss'

const getItemLabel = (item, format) => {
  if (typeof item === 'string') {
    return item
  }

  if (format == 'score') {
    format = 'overindex'
  }

  // get name
  let name = item.key || item.name
  if (name && name.length > 45) {
    name = `${name.slice(0, 45)}...`
  }

  // show item value if exists
  let value = ''
  if (item.value) {
    value = formatter(format, item.value)
  }

  return item.value ? `${name} (${value})` : name
}

const moveInArray = (arr, fromIndex, toIndex) => {
  const copyArr = [...arr]
  const element = copyArr[fromIndex]
  copyArr.splice(fromIndex, 1)
  copyArr.splice(toIndex, 0, element)
  return copyArr
}

const Item = ({ item, format, onRemove }) => (
  <div className="draggable-list__option">
    <Icon type="outline" icon="drag" color="currentColor" />
    {getItemLabel(item, format)}
    <Button
      theme={BUTTON_THEME.GHOST}
      className="draggable-list__remove-button"
      onClick={onRemove}
      value={JSON.stringify(item)}
      data-value={JSON.stringify(item)}
    >
      <Icon type="fill" icon="delete" color="currentColor" />
    </Button>
  </div>
)

const SortableItem = SortableElement((props) => <Item {...props} />)

const Items = ({ items, format, onRemove }) => (
  <For each="item" index="i" of={items}>
    <SortableItem
      // SORTABLE PROPS:
      index={i}
      // CUSTOM PROPS:
      key={i}
      item={item}
      format={format}
      onRemove={onRemove}
    />
  </For>
)

const SortableList = SortableContainer((props) => {
  return (
    <div className="draggable-list__container draggable-list__container--shown">
      <Items {...props} />
    </div>
  )
})

// component
const DraggableList = ({
  className,
  description,
  label,
  slideIndex,
  shown,
  shown_field,
  template,
  format,
  onRemove,
}) => {
  const handleSortEnd = ({ oldIndex, newIndex }) => {
    const newList = moveInArray(shown, oldIndex, newIndex)
    const payload = {
      field: shown_field,
      index: slideIndex,
      list: newList,
      name: template,
    }
    Events.emit('setListOrder', payload)
  }

  return (
    <div className={`draggable-list ${className}`.trim()}>
      <If condition={label}>
        <label className="draggable-list__label">{label}</label>
      </If>
      <If condition={description}>
        <h5>{description}</h5>
      </If>
      <Choose>
        <When condition={!!shown?.length}>
          <SortableList
            // SORTABLE PROPS:
            onSortEnd={handleSortEnd}
            axis="y"
            lockAxis="y"
            helperClass="draggable-list__option--dragging"
            distance={5}
            // CUSTOM PROPS:
            items={shown}
            format={format}
            onRemove={onRemove}
          />
        </When>
        <Otherwise>
          <p className="draggable-list__empty">No items selected</p>
        </Otherwise>
      </Choose>
    </div>
  )
}

DraggableList.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  format: PropTypes.string,
  slideIndex: PropTypes.number,
  label: PropTypes.string,
  template: PropTypes.string,
  onRemove: PropTypes.func,
  shown: PropTypes.array,
  shown_field: PropTypes.string,
}

DraggableList.defaultProps = {
  className: '',
  description: '',
  format: '',
  slideIndex: undefined,
  label: '',
  template: '',
  onRemove: () => {},
  shown: [],
  shown_field: '',
}

// export
export default DraggableList
